import { alpha, type Theme } from '@mui/material/styles';
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ThemeDirection, ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import React from 'react';
import { useSelector } from 'store';
import { closeRightSidebar } from 'store/slices/dual-sidebar';
import MainCard from 'ui-component/cards/MainCard';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import TranslateTwoToneIcon from '@mui/icons-material/TranslateTwoTone';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { IconTemplate } from '@tabler/icons-react';
import { ellipsisTextOverflowStyles } from 'themes/common/typography';
import ChatToolsVerticalRail, { CHAT_TOOLS_RAIL_WIDTH_PX } from './ChatToolsVerticalRail';
import {
  CHAT_LIST_PANEL_WIDTH_PX,
  CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING,
  CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM,
  CHAT_TRIPLEX_HEADER_OUTER_PY,
  LAYOUT_ZOOM_MIN_RIGHT_CHATS_PANEL_PX,
  chatTriplexHeaderBorderColor,
} from './layout-zoom-constants';
import { EXTENSION_PANEL_ID_PREFIX, isChatToolsDockedPanelId } from './chat-tools-rail-ids';
import { SIDEBAR_DRAWER_TRANSITION_MS } from './sidebarDrawerMotion';
import useLocalization from 'hooks/useLocalization';
import { getWhatsappTemplates } from 'store/slices/templates';
/** כמו `ChatUsersHeader` + שורת הכלים ב־`Filters.tsx` — גובה שורת אייקונים */
const CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX = 36;
/** כפתור סגירה עגול על הדופן הפנימי של הפאנל (מול הצ'אט) — כשאין מסילת כלים מצומדת */
const RIGHT_SIDEBAR_EDGE_CLOSE_DIAMETER_PX = 32;

function rightSidebarEdgeCloseIconButtonSx(theme: Theme) {
  return {
    width: RIGHT_SIDEBAR_EDGE_CLOSE_DIAMETER_PX,
    height: RIGHT_SIDEBAR_EDGE_CLOSE_DIAMETER_PX,
    p: 0,
    borderRadius: '50%',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: theme.shadows[2],
    color: 'text.secondary',
    '&:hover': {
      bgcolor: 'action.hover',
      borderColor: 'text.disabled',
    },
  } as const;
}

type RightSidebarEdgeCloseButtonProps = {
  onClose?: () => void;
};

function RightSidebarEdgeCloseButton({ onClose }: RightSidebarEdgeCloseButtonProps) {
  return (
    <Tooltip title="סגור" placement="right">
      <IconButton
        onClick={() => (onClose ? onClose() : closeRightSidebar())}
        aria-label="סגור"
        size="small"
        sx={(theme) => ({
          position: 'absolute',
          zIndex: 6,
          ...rightSidebarEdgeCloseIconButtonSx(theme),
          top: '50%',
          left: 0,
          right: 'auto',
          transform: 'translate(-50%, -50%)',
        })}
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} aria-hidden />
      </IconButton>
    </Tooltip>
  );
}

type ChatPanelHeaderTitleRowProps = {
  title: string;
  chatPanelTitleIcon: React.ReactNode;
  isTemplatesDrawerPanel: boolean;
  handleOpenTemplatesCreateInNewTab: () => void;
  onRefreshTemplates?: () => void;
  templatesRefreshing?: boolean;
  onClose?: () => void;
  /** במצב רייל+פאנל מאוחד — כפתור הסגירה יושב על קו התפר בין המסילה לתוכן */
  suppressEdgeCloseButton?: boolean;
};

/**
 * כותרת מוגדלת צמודה לצד המסילה (inline-start ב-RTL, inline-end ב-LTR).
 * Berry: themeDirection מ-useConfig + יישור לוגי — ב-RTL `flex-end` ב-Stack דוחף לשמאל (לא ליד הרייל).
 */
function ChatPanelHeaderTitleRow({
  title,
  chatPanelTitleIcon,
  isTemplatesDrawerPanel,
  handleOpenTemplatesCreateInNewTab,
  onRefreshTemplates,
  templatesRefreshing = false,
  onClose,
  suppressEdgeCloseButton = false,
}: ChatPanelHeaderTitleRowProps) {
  const { themeDirection } = useConfig();
  const { currentLocale } = useLocalization();
  const refreshTemplatesLabel =
    currentLocale.templatesMenu?.refreshTemplates ?? 'רענון רשימת תבניות';
  const isRtl = themeDirection === ThemeDirection.RTL;
  const handleRefreshTemplatesClick = () => {
    if (templatesRefreshing) return;
    onRefreshTemplates?.();
  };
  const actionBtn = isTemplatesDrawerPanel ? (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
      {onRefreshTemplates ? (
        <Tooltip title={refreshTemplatesLabel} placement="bottom" enterDelay={200} describeChild>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              type="button"
              onClick={handleRefreshTemplatesClick}
              aria-label={refreshTemplatesLabel}
              aria-busy={templatesRefreshing}
              size="small"
              sx={{
                width: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
                height: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
                p: 0.5,
                borderRadius: 1,
                color: 'grey.500',
                '&:hover': { bgcolor: 'action.hover', color: 'grey.700' },
              }}
            >
              <RefreshOutlinedIcon
                sx={{
                  fontSize: 18,
                  animation: templatesRefreshing
                    ? 'chat-templates-header-refresh-spin 0.85s linear infinite'
                    : 'none',
                  '@keyframes chat-templates-header-refresh-spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      <Tooltip title="פתיחת הגשת תבנית חדשה בטאב חדש" placement="bottom">
        <IconButton
          type="button"
          onClick={handleOpenTemplatesCreateInNewTab}
          aria-label="פתיחת מסך הגשת תבנית חדשה בטאב חדש"
          size="small"
          sx={{
            width: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
            height: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
            p: 0.5,
            borderRadius: 1,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : null;

  /** RTL: אייקון ליד המסילה (ימין), כותרת משמאל לו. LTR: כותרת משמאל, אייקון מימין */
  const titleBlock = (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent="flex-start"
      sx={{ flex: '1 1 auto', minWidth: 0 }}
    >
      {isRtl ? (
        <>
          {chatPanelTitleIcon ? (
            <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>{chatPanelTitleIcon}</Box>
          ) : null}
          <Typography
            component="div"
            variant="subtitle1"
            color="text.secondary"
            sx={{
              ...ellipsisTextOverflowStyles,
              fontWeight: 600,
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              textAlign: 'start',
              fontSize: '1.0625rem',
              minHeight: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            component="div"
            variant="subtitle1"
            color="text.secondary"
            sx={{
              ...ellipsisTextOverflowStyles,
              fontWeight: 600,
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              textAlign: 'start',
              fontSize: '1.0625rem',
              minHeight: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
          {chatPanelTitleIcon ? (
            <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>{chatPanelTitleIcon}</Box>
          ) : null}
        </>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
        boxSizing: 'border-box',
      }}
    >
      <Box
        /** מסילה מאוחדת עוטפת ב־`direction: ltr` — בלי `dir` כאן סדר ה-flex והריווח נשארים LTR */
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 1,
          minHeight: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
          flexDirection: 'row',
        }}
      >
        {isRtl ? (
          <>
            {titleBlock}
            {actionBtn}
          </>
        ) : (
          <>
            {actionBtn}
            {titleBlock}
          </>
        )}
      </Box>
      {!suppressEdgeCloseButton ? <RightSidebarEdgeCloseButton onClose={onClose} /> : null}
    </Box>
  );
}

function getTemplatesCreatePageAbsoluteUrl() {
  const raw = import.meta.env.VITE_APP_BASE_NAME || '';
  const base = raw.endsWith('/') ? raw.slice(0, -1) : raw;
  return `${window.location.origin}${base}/templates/create`;
}

interface RightAppSidebarProps {}

const RightAppSidebar: React.FC<RightAppSidebarProps> = () => {
  const { borderRadius } = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { open, width: widthRaw, title, hideHeader, onClose, activeId, chatPanelStyle } = useSelector(
    (state) => state.dualSidebar.right,
  );
  const whatsappTemplatesLoading = useSelector((state) => state.templates.whatsappTemplatesLoading);
  const width = widthRaw ?? CHAT_LIST_PANEL_WIDTH_PX;

  const isTemplatesPreview =
    activeId === 'templates-page-preview-sidebar' || activeId === 'templates-create-preview-sidebar';
  const useTemporaryVariant = isMobile;

  // ESC לסגירה — מובייל
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose ? onClose() : closeRightSidebar();
      }
    };

    if (open && isMobile) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, isMobile, onClose]);

  const drawerBG = theme.palette.background.paper;

  const panelTitleIconSx = { fontSize: 20, color: 'text.secondary', flexShrink: 0 } as const;
  const chatPanelTitleIcon =
    chatPanelStyle && activeId === 'chat-user-search-messages' ? (
      <SearchOutlinedIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'chat-user-conversations-menu' ? (
      <ForumOutlinedIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'contact-opt-out-log' ? (
      <HistoryOutlinedIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'chat-bot-status-menu' ? (
      <SmartToyOutlinedIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'ask-ai-dialog' ? (
      <AutoAwesomeOutlinedIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'contact-service-calls' ? (
      <SupportAgentOutlinedIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'chat-templates-menu' ? (
      <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', flexShrink: 0, lineHeight: 0 }}>
        <IconTemplate size={20} stroke={1.5} aria-hidden />
      </Box>
    ) : chatPanelStyle && activeId === 'chat-translation-panel' ? (
      <TranslateTwoToneIcon sx={panelTitleIconSx} />
    ) : chatPanelStyle && activeId === 'chat-user-details' ? (
      <PersonOutlineOutlinedIcon sx={panelTitleIconSx} />
    ) : null;

  const isTemplatesDrawerPanel = chatPanelStyle && activeId === 'chat-templates-menu';

  /** פאנלים שתוכנם הוא `AIAssistantPane` (כותרת+מסגרת משלהם) — מוצגים ללא ריפוד/כותרת כפולה (full-bleed) */
  const isEmbeddedAiPanel = Boolean(chatPanelStyle) && activeId === 'ask-ai-dialog';

  /** פאנל הרחבת שיחה — תוכן ממלא (iframe/sandbox) שמנהל את שטחו וגלילתו בעצמו */
  const isExtensionPanel =
    Boolean(chatPanelStyle) && Boolean(activeId?.startsWith(EXTENSION_PANEL_ID_PREFIX));

  /** פאנלים ללא ריפוד וללא גלילה חיצונית */
  const isFullBleedPanel = isEmbeddedAiPanel || isExtensionPanel;

  /** רייל + פאנל כיחידה אחת: זכוכית אחת, ללא רווח — רק דסקטופ ופאנלים ממסילת הכלים */
  const isUnifiedChatToolsDocked =
    open &&
    Boolean(chatPanelStyle && activeId && isChatToolsDockedPanelId(activeId)) &&
    !useTemporaryVariant;

  const slotWidth = React.useMemo(() => {
    if (useTemporaryVariant) {
      if (isMobile) return open ? '90vw' : 0;
      return open ? width : 0;
    }
    if (isMobile) return open ? '90vw' : 0;
    if (open) return isUnifiedChatToolsDocked ? width + CHAT_TOOLS_RAIL_WIDTH_PX : width;
    return 0;
  }, [useTemporaryVariant, isMobile, open, width, isUnifiedChatToolsDocked]);

  const desktopDrawerPaperWidth = React.useMemo(
    () =>
      open && isUnifiedChatToolsDocked ? width + CHAT_TOOLS_RAIL_WIDTH_PX : width,
    [open, isUnifiedChatToolsDocked, width],
  );

  const handleOpenTemplatesCreateInNewTab = () => {
    window.open(getTemplatesCreatePageAbsoluteUrl(), '_blank', 'noopener,noreferrer');
  };

  const handleRefreshTemplatesFromHeader = () => {
    if (whatsappTemplatesLoading) return;
    void getWhatsappTemplates();
  };

  const chatToolsDockedHeaderSx =
    chatPanelStyle && isUnifiedChatToolsDocked
      ? {
          pt: CHAT_TRIPLEX_HEADER_OUTER_PY,
          pb: CHAT_TRIPLEX_HEADER_OUTER_PY,
          px: 0,
          bgcolor: 'transparent',
          transition: (t: Theme) =>
            t.transitions.create(['background-color', 'border-color'], { duration: 200 }),
          boxSizing: 'border-box' as const,
          borderBottom: '1px solid',
          borderColor: chatTriplexHeaderBorderColor(theme),
        }
      : null;

  const chatPanelHeaderOuterSx =
    chatPanelStyle && !isUnifiedChatToolsDocked
      ? {
          pt: CHAT_TRIPLEX_HEADER_OUTER_PY,
          pb: CHAT_TRIPLEX_HEADER_OUTER_PY,
          px: 0,
          bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? alpha(t.palette.grey[900], 0.45) : '#F8F7FA'),
          transition: (t: Theme) =>
            t.transitions.create(['background-color', 'border-color'], { duration: 200 }),
          boxSizing: 'border-box' as const,
          borderBottom: '1px solid',
          borderColor: chatTriplexHeaderBorderColor(theme),
        }
      : null;

  const sidebarColumn = (
    <>
      {!hideHeader && (
        <Box
          sx={
            chatPanelStyle
              ? chatToolsDockedHeaderSx ?? chatPanelHeaderOuterSx
              : {
                  px: { xs: 1.5, md: 2.5 },
                  pt: 0,
                }
          }
        >
          {chatPanelStyle ? (
            <Box sx={{ mb: CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM }}>
              <Box
                style={{
                  paddingLeft: theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                  paddingRight: theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                  boxSizing: 'border-box',
                }}
                sx={{
                  position: 'relative',
                  minHeight: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ChatPanelHeaderTitleRow
                  title={title ?? ''}
                  chatPanelTitleIcon={chatPanelTitleIcon}
                  isTemplatesDrawerPanel={Boolean(isTemplatesDrawerPanel)}
                  handleOpenTemplatesCreateInNewTab={handleOpenTemplatesCreateInNewTab}
                  onRefreshTemplates={isTemplatesDrawerPanel ? handleRefreshTemplatesFromHeader : undefined}
                  templatesRefreshing={isTemplatesDrawerPanel ? whatsappTemplatesLoading : false}
                  onClose={onClose}
                  suppressEdgeCloseButton={isUnifiedChatToolsDocked}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                pb: 1.25,
                pr: 0,
                mr: { xs: -1.5, md: -2.5 },
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                minHeight: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
              }}
            >
              <Grid
                container
                spacing={1.5}
                alignItems="center"
                width="100%"
                wrap="nowrap"
                sx={{ paddingInlineStart: RIGHT_SIDEBAR_EDGE_CLOSE_DIAMETER_PX / 2 }}
              >
                <Grid size="grow" overflow="hidden" sx={{ minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <Typography
                      component="div"
                      variant="h4"
                      color="text.primary"
                      sx={{
                        ...ellipsisTextOverflowStyles,
                        fontWeight: 500,
                      }}
                    >
                      {title}
                    </Typography>
                  </Stack>
                </Grid>
                {isTemplatesDrawerPanel ? (
                  <Grid size="auto" sx={{ flexShrink: 0 }}>
                    <Tooltip title="פתיחת הגשת תבנית חדשה בטאב חדש" placement="bottom">
                      <IconButton
                        type="button"
                        onClick={handleOpenTemplatesCreateInNewTab}
                        aria-label="פתיחת מסך הגשת תבנית חדשה בטאב חדש"
                        size="small"
                        sx={{
                          width: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
                          height: CHAT_LIST_TOOLBAR_ROW_MIN_HEIGHT_PX,
                          p: 0.5,
                          borderRadius: 1,
                          color: 'text.secondary',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ) : null}
              </Grid>
              <RightSidebarEdgeCloseButton onClose={onClose} />
            </Box>
          )}
          {!chatPanelStyle ? <Divider /> : null}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          flexGrow: 1,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          id={'right-sidebar-content-wrapper'}
          sx={{
            width: '100%',
            minWidth: 0,
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            overflowY: isFullBleedPanel ? 'hidden' : 'auto',
            /**
             * תבניות: הגלילה ברשימה הפנימית (`List`); `stable` כאן משאיר רצועה ריקה בצד בלי פס גלילה.
             */
            scrollbarGutter:
              isFullBleedPanel
                ? 'auto'
                : activeId === 'chat-templates-menu' && chatPanelStyle
                  ? 'auto'
                  : 'stable',
            scrollBehavior: isTemplatesDrawerPanel && open ? 'smooth' : 'auto',
            '@media (prefers-reduced-motion: reduce)': {
              scrollBehavior: 'auto',
            },
            overscrollBehaviorY: 'contain',
            WebkitOverflowScrolling: 'touch',
            pt:
              isFullBleedPanel
                ? 0
                : activeId === 'chat-templates-menu' && chatPanelStyle
                  ? 0
                  : chatPanelStyle
                    ? 1.25
                    : { xs: 1.5, md: 2.5 },
            ...(isFullBleedPanel
              ? { px: 0 }
              : activeId === 'chat-templates-menu' && chatPanelStyle
                ? { px: 0 }
                : chatPanelStyle
                  ? {
                      /** כמו שורת כותרת הפאנל + `ChatPanelHeaderTitleRow` — ריפוד פיזי סימטרי; `pl:0 pr:grid` שבר יישור ב־RTL מול המסילה */
                      px: (theme) => theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                    }
                  : { px: { xs: 1.5, md: 2.5 } }),
            pb: isFullBleedPanel ? 0 : 0.5,
            ...(isTemplatesPreview && {
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }),
          }}
        ></Box>

        {isMobile && isTemplatesPreview && (
          <Tooltip title="סגור" placement="right">
            <IconButton
              onClick={() => (onClose ? onClose() : closeRightSidebar())}
              aria-label="סגור"
              size="small"
              sx={(t) => ({
                position: 'absolute',
                top: 16,
                left: 0,
                right: 'auto',
                zIndex: 1000,
                transform: 'translate(-50%, 0)',
                width: RIGHT_SIDEBAR_EDGE_CLOSE_DIAMETER_PX,
                height: RIGHT_SIDEBAR_EDGE_CLOSE_DIAMETER_PX,
                p: 0,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: t.shadows[3],
                '&:hover': {
                  bgcolor: 'action.hover',
                  boxShadow: t.shadows[6],
                },
              })}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} aria-hidden />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        width: slotWidth,
        flexShrink: 0,
        minWidth:
          open && !useTemporaryVariant && !isMobile ? LAYOUT_ZOOM_MIN_RIGHT_CHATS_PANEL_PX : undefined,
        height: '100%',
        position: 'relative',
        /** persistent drawer stays z-index 0 — must not exceed AppBar (1100) / nav drawer (1099) */
        zIndex: {
          xs: 1100,
          md: isTemplatesPreview ? 1100 : 0,
        },
        transition: isTemplatesPreview
          ? 'none'
          : theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: SIDEBAR_DRAWER_TRANSITION_MS.enter,
            }),
        mr: 0,
        ml: 0,
      }}
    >
      <Drawer
        sx={{
          width: open ? '100%' : 0,
          flexShrink: 0,
          height: '100%',
          overflow: 'hidden',
          pointerEvents: open ? 'auto' : 'none',
          '& .MuiDrawer-paper': {
          height: (isMobile && isTemplatesPreview) ? '80vh' : '100%', // 80% height only for templates preview on mobile
          width: isMobile ? '90vw' : desktopDrawerPaperWidth,
          maxWidth: isMobile ? '400px' : 'none', // Maximum 400px on mobile
          transition:
            isTemplatesPreview || !open
              ? isTemplatesPreview
                ? 'none'
                : undefined
              : theme.transitions.create(['transform', 'width'], {
                  duration: SIDEBAR_DRAWER_TRANSITION_MS.enter,
                  easing: theme.transitions.easing.easeInOut,
                }),
          boxSizing: 'border-box',
          position: 'relative',
          border: 'none',
          ...(isUnifiedChatToolsDocked && open
            ? {
                bgcolor: 'transparent',
                boxShadow: 'none',
                borderRadius: { xs: 0, md: 0 },
              }
            : {
                borderRadius: { sx: 0, md: `${borderRadius}px` },
              }),
          overflow:
            open && (isUnifiedChatToolsDocked || isTemplatesDrawerPanel) ? 'visible' : 'hidden',
          ...(isMobile && isTemplatesPreview && {
            top: '10%',
            transform: 'translateY(-50%)',
            margin: 'auto',
          }),
        },
      }}
      variant={useTemporaryVariant ? 'temporary' : 'persistent'}
      anchor="right"
      open={open}
      ModalProps={{
        keepMounted: true,
      }}
      onClose={() => (onClose ? onClose() : closeRightSidebar())}
      transitionDuration={isTemplatesPreview ? 0 : SIDEBAR_DRAWER_TRANSITION_MS}
      SlideProps={
        isTemplatesPreview
          ? undefined
          : {
              easing: theme.transitions.easing.easeInOut,
            }
      }
    >
      <MainCard
        sx={
          isUnifiedChatToolsDocked
            ? {
                bgcolor: 'transparent',
                height: '100%',
                py: 0,
                border: 'none',
                position: 'relative',
                overflow: 'visible',
                boxShadow: 'none',
              }
            : {
                bgcolor: { xs: 'transparent', [theme.breakpoints.down('md')]: drawerBG },
                height: '100%',
                py: chatPanelStyle ? 0 : 1.25,
                border: 'none',
                position: 'relative',
                overflow: isTemplatesDrawerPanel ? 'visible' : undefined,
              }
        }
        border={isUnifiedChatToolsDocked ? false : theme.palette.mode !== ThemeMode.DARK}
        content={false}
        elevation={0}
      >
        {isUnifiedChatToolsDocked ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              direction: 'ltr',
              height: '100%',
              minHeight: 0,
              borderRadius: { xs: 0, md: `${borderRadius}px` },
              overflow: 'visible',
              boxSizing: 'border-box',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.background.paper, t.palette.mode === 'dark' ? 0.38 : 0.72),
              backdropFilter: 'blur(18px) saturate(1.2)',
              WebkitBackdropFilter: 'blur(18px) saturate(1.2)',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? `0 18px 52px ${alpha(theme.palette.common.black, 0.55)}`
                  : `0 18px 52px ${alpha(theme.palette.common.black, 0.14)}`,
            }}
          >
            <Stack direction="column" sx={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%' }}>
              {sidebarColumn}
            </Stack>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: (t) => alpha(t.palette.divider, 0.4),
                alignSelf: 'stretch',
                my: 0,
              }}
            />
            <ChatToolsVerticalRail
              variant="docked"
              onDockedClose={() => (onClose ? onClose() : closeRightSidebar())}
            />
          </Box>
        ) : (
          <Stack direction="column" height="100%">
            {sidebarColumn}
          </Stack>
        )}
      </MainCard>
    </Drawer>
    </Box>
  );
};

export default RightAppSidebar; 